import React, { useState, useEffect } from 'react';
import { Save, Loader2, CreditCard, Mail, Bell, Globe, Lock, Building2, CheckCircle, AlertCircle, Info, FolderUp } from 'lucide-react';
import { getGeneralSettings, updateGeneralSettings, getStripeKeyStatus } from '../firebase/settings';
import { getPayPalKeyStatus } from '../firebase/paypal';
import { useToast } from './Toast';
import { HelpTooltip, FeatureGuide, InfoBanner } from './HelpTooltip';
import { TableSkeleton } from './LoadingStates';

export function AdminSettingsTab() {
  const { showToast } = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState(null);
  const [activeSection, setActiveSection] = useState('business');

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    setLoading(true);
    try {
      const data = await getGeneralSettings();
      if (data) {
        setSettings(data);
      }
    } catch (error) {
      console.error('Error loading settings:', error);
      showToast('Failed to load settings', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const result = await updateGeneralSettings(settings);
      if (result.success) {
        showToast('Settings saved successfully', 'success');
      } else {
        showToast('Error saving settings: ' + result.error, 'error');
      }
    } catch (error) {
      showToast('Error saving settings: ' + error.message, 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field, value) => {
    setSettings(prev => ({ ...prev, [field]: value }));
  };

  const handleNestedChange = (parent, field, value) => {
    setSettings(prev => ({
      ...prev,
      [parent]: {
        ...prev[parent],
        [field]: value
      }
    }));
  };

  const stripeStatus = getStripeKeyStatus();
  const paypalStatus = getPayPalKeyStatus();

  if (loading) {
    return <TableSkeleton rows={5} />;
  }

  if (!settings) {
    return (
      <div className="bg-white rounded-xl shadow-md border border-gray-200 p-12 text-center">
        <AlertCircle className="w-16 h-16 text-gray-300 mx-auto mb-4" />
        <p className="text-gray-500">Failed to load settings</p>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <h2 className="text-2xl font-semibold text-gray-800">General Settings</h2>
          <HelpTooltip
            title="General Settings"
            content="Configure your business information, payment settings, email service, notifications, system preferences, and security settings. These settings control how your storefront operates and how you receive notifications."
          />
        </div>
        <button
          onClick={handleSave}
          disabled={saving}
          className="px-6 py-2.5 bg-[#c5a059] text-white rounded-lg hover:bg-[#b08e4d] transition flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          {saving ? 'Saving...' : 'Save Settings'}
        </button>
      </div>

      <FeatureGuide
        title="General Settings Guide"
        description="Manage all your business configuration in one place. Update business information, payment settings, email service configuration, notification preferences, system settings, and security options."
        steps={[
          "Navigate between sections using the tabs above",
          "Fill in your business information (name, email, phone, address)",
          "Configure payment settings (currency, payment methods)",
          "Set up email service provider and credentials",
          "Choose which notifications you want to receive",
          "Adjust system settings (timezone, date format, language)",
          "Configure security settings (session timeout, 2FA)",
          "Click 'Save Settings' to apply all changes"
        ]}
        tips={[
          "Business information is used in emails and receipts",
          "Stripe key must be set in environment variables (VITE_STRIPE_PUBLISHABLE_KEY)",
          "Email service must be configured for automated emails to work",
          "Notification settings control what emails you receive",
          "Timezone affects booking times and date displays",
          "Session timeout automatically logs you out after inactivity"
        ]}
      />

      {/* Section Navigation */}
      <div className="bg-white rounded-xl shadow-md border border-gray-200 mb-6">
        <div className="flex flex-wrap gap-2 p-4 border-b border-gray-200">
          {[
            { id: 'business', label: 'Business Info', icon: Building2 },
            { id: 'payment', label: 'Payment', icon: CreditCard },
            { id: 'email', label: 'Email Service', icon: Mail },
            { id: 'sftp', label: 'SFTP Files', icon: FolderUp },
            { id: 'notifications', label: 'Notifications', icon: Bell },
            { id: 'system', label: 'System', icon: Globe },
            { id: 'security', label: 'Security', icon: Lock }
          ].map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              onClick={() => setActiveSection(id)}
              className={`px-4 py-2 rounded-lg transition flex items-center gap-2 ${activeSection === id
                  ? 'bg-[#c5a059] text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
            >
              <Icon size={18} />
              <span className="text-sm font-medium">{label}</span>
            </button>
          ))}
        </div>

        <div className="p-6">
          {/* Business Information */}
          {activeSection === 'business' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                  <Building2 size={20} />
                  Business Information
                </h3>
                <InfoBanner type="info" title="Business Information">
                  This information is used in emails, receipts, invoices, and displayed on your storefront. Keep it up to date for professional communication.
                </InfoBanner>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Business Name *
                  </label>
                  <input
                    type="text"
                    value={settings.businessName || ''}
                    onChange={(e) => handleChange('businessName', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#c5a059] focus:border-[#c5a059]"
                    placeholder="Your Business Name"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Business Email *
                  </label>
                  <input
                    type="email"
                    value={settings.businessEmail || ''}
                    onChange={(e) => handleChange('businessEmail', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#c5a059] focus:border-[#c5a059]"
                    placeholder="hello@yourbusiness.com"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Business Phone
                  </label>
                  <input
                    type="tel"
                    value={settings.businessPhone || ''}
                    onChange={(e) => handleChange('businessPhone', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#c5a059] focus:border-[#c5a059]"
                    placeholder="+44 20 1234 5678"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Business Website
                  </label>
                  <input
                    type="url"
                    value={settings.businessWebsite || ''}
                    onChange={(e) => handleChange('businessWebsite', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#c5a059] focus:border-[#c5a059]"
                    placeholder="https://yourwebsite.com"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Business Address
                  </label>
                  <textarea
                    value={settings.businessAddress || ''}
                    onChange={(e) => handleChange('businessAddress', e.target.value)}
                    rows={3}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#c5a059] focus:border-[#c5a059]"
                    placeholder="123 Business Street, City, Postcode, Country"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Tax ID / VAT Number
                  </label>
                  <input
                    type="text"
                    value={settings.taxId || settings.vatNumber || ''}
                    onChange={(e) => {
                      handleChange('taxId', e.target.value);
                      handleChange('vatNumber', e.target.value);
                    }}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#c5a059] focus:border-[#c5a059]"
                    placeholder="GB123456789"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Payment Settings */}
          {activeSection === 'payment' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                  <CreditCard size={20} />
                  Payment Configuration
                </h3>
                <InfoBanner type="info" title="Payment Settings">
                  Configure your payment methods and currency. PayPal is the default payment method. Set VITE_PAYPAL_CLIENT_ID in your environment variables for PayPal to work.
                </InfoBanner>
              </div>

              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Currency
                  </label>
                  <select
                    value={settings.currency || 'GBP'}
                    onChange={(e) => handleChange('currency', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#c5a059] focus:border-[#c5a059]"
                  >
                    <option value="GBP">GBP (£)</option>
                    <option value="USD">USD ($)</option>
                    <option value="EUR">EUR (€)</option>
                    <option value="CAD">CAD (C$)</option>
                    <option value="AUD">AUD (A$)</option>
                  </select>
                </div>

                {/* PayPal Status - Primary Payment Method */}
                <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-sm font-medium text-gray-700">
                      PayPal Client ID Status
                    </label>
                    {paypalStatus.configured ? (
                      <span className="flex items-center gap-2 text-green-600">
                        <CheckCircle size={16} />
                        <span className="text-sm">Configured ({paypalStatus.mode})</span>
                      </span>
                    ) : (
                      <span className="flex items-center gap-2 text-yellow-600">
                        <AlertCircle size={16} />
                        <span className="text-sm">Not Configured</span>
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-gray-500 mb-2">
                    {paypalStatus.configured
                      ? `Key: ${paypalStatus.masked}`
                      : 'Set VITE_PAYPAL_CLIENT_ID in your environment variables'}
                  </p>
                  <p className="text-xs text-gray-500">
                    {paypalStatus.configured
                      ? `PayPal payments are enabled in ${paypalStatus.mode} mode`
                      : 'PayPal payments will not work until configured'}
                  </p>
                </div>

                {/* Stripe Status - Disabled */}
                <div className="bg-gray-50 rounded-lg p-4 border border-gray-200 opacity-60">
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-sm font-medium text-gray-700">
                      Stripe Publishable Key Status
                    </label>
                    {stripeStatus.configured ? (
                      <span className="flex items-center gap-2 text-green-600">
                        <CheckCircle size={16} />
                        <span className="text-sm">Configured</span>
                      </span>
                    ) : (
                      <span className="flex items-center gap-2 text-gray-400">
                        <AlertCircle size={16} />
                        <span className="text-sm">Disabled</span>
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-gray-500">
                    Stripe is currently disabled. PayPal is the active payment method.
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    Payment Methods
                  </label>
                  <div className="space-y-3">
                    <label className="flex items-center gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={settings.paymentMethods?.paypal ?? true}
                        onChange={(e) => handleNestedChange('paymentMethods', 'paypal', e.target.checked)}
                        className="w-4 h-4 text-[#c5a059] border-gray-300 rounded focus:ring-[#c5a059]"
                      />
                      <span className="text-sm text-gray-700">PayPal (Default)</span>
                      {paypalStatus.configured && (
                        <span className="text-xs text-green-600 bg-green-50 px-2 py-0.5 rounded">Active</span>
                      )}
                    </label>
                    <label className="flex items-center gap-3 cursor-not-allowed opacity-50">
                      <input
                        type="checkbox"
                        checked={settings.paymentMethods?.stripe || false}
                        onChange={(e) => handleNestedChange('paymentMethods', 'stripe', e.target.checked)}
                        className="w-4 h-4 text-gray-300 border-gray-300 rounded"
                        disabled
                      />
                      <span className="text-sm text-gray-500">Stripe (Credit/Debit Cards) - Disabled</span>
                    </label>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Email Service Settings */}
          {activeSection === 'email' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                  <Mail size={20} />
                  Email Service Configuration
                </h3>
                <InfoBanner type="info" title="Email Service">
                  Configure your email service provider to enable automated emails (welcome emails, order confirmations, booking reminders, etc.). Email templates are managed in the Email tab.
                </InfoBanner>
              </div>

              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email Service Provider
                  </label>
                  <select
                    value={settings.emailServiceProvider || 'none'}
                    onChange={(e) => handleChange('emailServiceProvider', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#c5a059] focus:border-[#c5a059]"
                  >
                    <option value="none">None (Email features disabled)</option>
                    <option value="smtp">SMTP (Your Own Mailbox - IONOS)</option>
                    <option value="firebase-extension">Firebase Extension (Trigger Email)</option>
                    <option value="emailjs">EmailJS</option>
                    <option value="sendgrid">SendGrid</option>
                    <option value="resend">Resend</option>
                    <option value="custom">Custom API</option>
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    Choose your email service provider. SMTP uses your IONOS mailbox (Fabiha@freespiritfabiha.com).
                  </p>
                </div>

                {settings.emailServiceProvider === 'smtp' && (
                  <div className="space-y-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <h4 className="font-semibold text-blue-900">SMTP Configuration (IONOS)</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          SMTP Server
                        </label>
                        <input
                          type="text"
                          value={settings.smtpHost || 'smtp.ionos.co.uk'}
                          onChange={(e) => handleChange('smtpHost', e.target.value)}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#c5a059] focus:border-[#c5a059]"
                          placeholder="smtp.ionos.co.uk"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          SMTP Port
                        </label>
                        <input
                          type="number"
                          value={settings.smtpPort || '465'}
                          onChange={(e) => handleChange('smtpPort', parseInt(e.target.value))}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#c5a059] focus:border-[#c5a059]"
                          placeholder="465"
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          IONOS requires port 465 with SSL (not 587)
                        </p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Email Address
                        </label>
                        <input
                          type="email"
                          value={settings.smtpUser || 'Fabiha@freespiritfabiha.com'}
                          onChange={(e) => handleChange('smtpUser', e.target.value)}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#c5a059] focus:border-[#c5a059]"
                          placeholder="Fabiha@freespiritfabiha.com"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Password
                        </label>
                        <input
                          type="password"
                          value={settings.smtpPassword || ''}
                          onChange={(e) => handleChange('smtpPassword', e.target.value)}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#c5a059] focus:border-[#c5a059]"
                          placeholder="Your email password"
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          Password is stored securely in Firestore. Also set in Firebase Functions config for Cloud Function.
                        </p>
                      </div>
                    </div>
                    <div className="bg-yellow-50 border-l-4 border-yellow-400 p-3 rounded">
                      <p className="text-xs text-yellow-800">
                        <strong>Note:</strong> After setting the password here, you also need to set it in Firebase Functions config:
                        <code className="block mt-1 bg-yellow-100 p-2 rounded">firebase functions:config:set smtp.password="YOUR_PASSWORD"</code>
                      </p>
                    </div>
                  </div>
                )}

                {settings.emailServiceProvider !== 'none' && settings.emailServiceProvider !== 'smtp' && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Email Service API URL
                      </label>
                      <input
                        type="url"
                        value={settings.emailServiceUrl || ''}
                        onChange={(e) => handleChange('emailServiceUrl', e.target.value)}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#c5a059] focus:border-[#c5a059]"
                        placeholder="https://api.emailservice.com/send"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        API endpoint for sending emails (set VITE_EMAIL_SERVICE_URL in environment variables)
                      </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          From Name
                        </label>
                        <input
                          type="text"
                          value={settings.emailFromName || settings.businessName || ''}
                          onChange={(e) => handleChange('emailFromName', e.target.value)}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#c5a059] focus:border-[#c5a059]"
                          placeholder="Your Business Name"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          From Email Address
                        </label>
                        <input
                          type="email"
                          value={settings.emailFromAddress || settings.businessEmail || ''}
                          onChange={(e) => handleChange('emailFromAddress', e.target.value)}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#c5a059] focus:border-[#c5a059]"
                          placeholder="noreply@yourbusiness.com"
                        />
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          )}

          {/* SFTP Settings */}
          {activeSection === 'sftp' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                  <FolderUp size={20} />
                  SFTP File Upload Configuration
                </h3>
                <InfoBanner type="info" title="SFTP File Storage">
                  Configure your SFTP server settings for file uploads. Files uploaded from the email editor will be stored on your SFTP server and linked in emails. The password must also be set as a Firebase secret for security.
                </InfoBanner>
              </div>

              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      SFTP Host
                    </label>
                    <input
                      type="text"
                      value={settings.sftpHost || 'access-5019186208.webspace-host.com'}
                      onChange={(e) => handleChange('sftpHost', e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#c5a059] focus:border-[#c5a059]"
                      placeholder="access-5019186208.webspace-host.com"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Your SFTP server hostname
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      SFTP Port
                    </label>
                    <input
                      type="number"
                      value={settings.sftpPort || 22}
                      onChange={(e) => handleChange('sftpPort', parseInt(e.target.value) || 22)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#c5a059] focus:border-[#c5a059]"
                      placeholder="22"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      SFTP port (usually 22)
                    </p>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    SFTP Username
                  </label>
                  <input
                    type="text"
                    value={settings.sftpUsername || 'su247761'}
                    onChange={(e) => handleChange('sftpUsername', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#c5a059] focus:border-[#c5a059]"
                    placeholder="su247761"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Your SFTP account username
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    SFTP Password (Optional - set as Firebase Secret for security)
                  </label>
                  <input
                    type="password"
                    value={settings.sftpPassword || ''}
                    onChange={(e) => handleChange('sftpPassword', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#c5a059] focus:border-[#c5a059]"
                    placeholder="••••••••••••"
                  />
                  <div className="bg-blue-50 border-l-4 border-blue-400 p-3 rounded mt-2">
                    <p className="text-xs text-blue-800">
                      <strong>Security Note:</strong> For production, set the password as a Firebase secret instead of storing it here:
                      <code className="block mt-1 bg-blue-100 p-2 rounded font-mono text-xs">
                        firebase functions:secrets:set SFTP_PASSWORD
                      </code>
                      The Cloud Function will use the secret if available, otherwise it will use the password stored here.
                    </p>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Default Upload Folder
                  </label>
                  <input
                    type="text"
                    value={settings.sftpDefaultFolder || 'email-files'}
                    onChange={(e) => handleChange('sftpDefaultFolder', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#c5a059] focus:border-[#c5a059]"
                    placeholder="email-files"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Default folder where files will be uploaded (e.g., "email-files", "uploads")
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Public File URL Base
                  </label>
                  <input
                    type="url"
                    value={settings.sftpPublicUrlBase || 'https://freespiritfabiha.com'}
                    onChange={(e) => handleChange('sftpPublicUrlBase', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#c5a059] focus:border-[#c5a059]"
                    placeholder="https://freespiritfabiha.com"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Base URL where uploaded files will be accessible (e.g., https://freespiritfabiha.com)
                  </p>
                </div>

                <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded">
                  <p className="text-sm text-yellow-800">
                    <strong>Important:</strong> Make sure your SFTP server allows connections from Google Cloud Functions IP addresses. If you're experiencing connection issues, you may need to whitelist Google Cloud IP ranges or contact your hosting provider.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Notification Settings */}
          {activeSection === 'notifications' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                  <Bell size={20} />
                  Notification Preferences
                </h3>
                <InfoBanner type="info" title="Notifications">
                  Choose which events trigger email notifications. You'll receive emails at the address specified below when these events occur.
                </InfoBanner>
              </div>

              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Notification Email Address
                  </label>
                  <input
                    type="email"
                    value={settings.notificationEmail || settings.businessEmail || ''}
                    onChange={(e) => handleChange('notificationEmail', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#c5a059] focus:border-[#c5a059]"
                    placeholder="notifications@yourbusiness.com"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Email address where you'll receive notifications
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    Email Notifications
                  </label>
                  <div className="space-y-3">
                    {[
                      { key: 'newOrder', label: 'New Order Received', desc: 'Get notified when a customer places an order' },
                      { key: 'newBooking', label: 'New Booking Request', desc: 'Get notified when someone books a session' },
                      { key: 'newSubscriber', label: 'New Email Subscriber', desc: 'Get notified when someone joins your mailing list' },
                      { key: 'paymentReceived', label: 'Payment Received', desc: 'Get notified when a payment is completed' },
                      { key: 'bookingCancelled', label: 'Booking Cancelled', desc: 'Get notified when a booking is cancelled' }
                    ].map(({ key, label, desc }) => (
                      <label key={key} className="flex items-start gap-3 cursor-pointer p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition">
                        <input
                          type="checkbox"
                          checked={settings.notifications?.[key] || false}
                          onChange={(e) => handleNestedChange('notifications', key, e.target.checked)}
                          className="w-4 h-4 mt-0.5 text-[#c5a059] border-gray-300 rounded focus:ring-[#c5a059]"
                        />
                        <div>
                          <span className="text-sm font-medium text-gray-700 block">{label}</span>
                          <span className="text-xs text-gray-500">{desc}</span>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* System Settings */}
          {activeSection === 'system' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                  <Globe size={20} />
                  System Preferences
                </h3>
                <InfoBanner type="info" title="System Settings">
                  Configure how dates, times, and data are displayed and refreshed throughout the admin dashboard.
                </InfoBanner>
              </div>

              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Timezone
                  </label>
                  <select
                    value={settings.timezone || 'Europe/London'}
                    onChange={(e) => handleChange('timezone', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#c5a059] focus:border-[#c5a059]"
                  >
                    <option value="Europe/London">Europe/London (GMT)</option>
                    <option value="America/New_York">America/New_York (EST)</option>
                    <option value="America/Los_Angeles">America/Los_Angeles (PST)</option>
                    <option value="Europe/Paris">Europe/Paris (CET)</option>
                    <option value="Asia/Dubai">Asia/Dubai (GST)</option>
                    <option value="Australia/Sydney">Australia/Sydney (AEDT)</option>
                  </select>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Date Format
                    </label>
                    <select
                      value={settings.dateFormat || 'DD/MM/YYYY'}
                      onChange={(e) => handleChange('dateFormat', e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#c5a059] focus:border-[#c5a059]"
                    >
                      <option value="DD/MM/YYYY">DD/MM/YYYY (31/12/2024)</option>
                      <option value="MM/DD/YYYY">MM/DD/YYYY (12/31/2024)</option>
                      <option value="YYYY-MM-DD">YYYY-MM-DD (2024-12-31)</option>
                      <option value="DD MMM YYYY">DD MMM YYYY (31 Dec 2024)</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Time Format
                    </label>
                    <select
                      value={settings.timeFormat || '12h'}
                      onChange={(e) => handleChange('timeFormat', e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#c5a059] focus:border-[#c5a059]"
                    >
                      <option value="12h">12-hour (2:30 PM)</option>
                      <option value="24h">24-hour (14:30)</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Language
                  </label>
                  <select
                    value={settings.language || 'en'}
                    onChange={(e) => handleChange('language', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#c5a059] focus:border-[#c5a059]"
                  >
                    <option value="en">English</option>
                    <option value="es">Spanish</option>
                    <option value="fr">French</option>
                    <option value="de">German</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Auto-Refresh Interval (seconds)
                  </label>
                  <input
                    type="number"
                    min="10"
                    max="300"
                    value={settings.autoRefreshInterval || 30}
                    onChange={(e) => handleChange('autoRefreshInterval', parseInt(e.target.value))}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#c5a059] focus:border-[#c5a059]"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    How often the dashboard automatically refreshes data (10-300 seconds)
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Security Settings */}
          {activeSection === 'security' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                  <Lock size={20} />
                  Security Settings
                </h3>
                <InfoBanner type="info" title="Security">
                  Configure security settings for your admin account. Admin access is managed via Firestore admins collection.
                </InfoBanner>
              </div>

              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Session Timeout (minutes)
                  </label>
                  <input
                    type="number"
                    min="5"
                    max="480"
                    value={settings.sessionTimeout || 60}
                    onChange={(e) => handleChange('sessionTimeout', parseInt(e.target.value))}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#c5a059] focus:border-[#c5a059]"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Automatically log out after this many minutes of inactivity (5-480 minutes)
                  </p>
                </div>

                <div className="space-y-3">
                  <label className="flex items-center gap-3 cursor-pointer p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition">
                    <input
                      type="checkbox"
                      checked={settings.requirePasswordChange || false}
                      onChange={(e) => handleChange('requirePasswordChange', e.target.checked)}
                      className="w-4 h-4 text-[#c5a059] border-gray-300 rounded focus:ring-[#c5a059]"
                    />
                    <div>
                      <span className="text-sm font-medium text-gray-700 block">Require Password Change</span>
                      <span className="text-xs text-gray-500">Force password change on next login (Coming Soon)</span>
                    </div>
                  </label>

                  <label className="flex items-center gap-3 cursor-pointer p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition">
                    <input
                      type="checkbox"
                      checked={settings.twoFactorEnabled || false}
                      onChange={(e) => handleChange('twoFactorEnabled', e.target.checked)}
                      className="w-4 h-4 text-[#c5a059] border-gray-300 rounded focus:ring-[#c5a059]"
                    />
                    <div>
                      <span className="text-sm font-medium text-gray-700 block">Two-Factor Authentication</span>
                      <span className="text-xs text-gray-500">Enable 2FA for additional security (Coming Soon)</span>
                    </div>
                  </label>
                </div>

                <div className="bg-blue-50 border-l-4 border-blue-400 p-4 rounded-lg">
                  <div className="flex items-start gap-3">
                    <Info size={20} className="text-blue-600 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-blue-800 mb-1">Admin Access Management</p>
                      <p className="text-xs text-blue-700">
                        Admin accounts are managed via Firestore. To add or remove admins, create or delete documents in the <code className="bg-blue-100 px-1 rounded">admins</code> collection with the admin's email as the document ID.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}



